import { memo } from 'react';
import { 
  getCommunicationFrequencyLevel, 
  getCommunicationStyleLevel, 
  getStrategicInfluenceLevel, 
  getResultVisibilityLevel 
} from '../utils/compensationPartner/levelMapper';

/**
 * MarketAlignmentAnalysis Component
 * 
 * Analyzes salary progression alignment between internal and market standards
 * Shows multi-source market comparison with detailed deviation analysis
 * 
 * @param {Array} positions - Array of position objects with salary and requirement data
 * @param {Function} shortenPositionName - Function to shorten position names for display
 * @param {Object} visibleSources - Object controlling which market sources to show
 * @param {boolean} showTitle - Whether to show the section title (default: true)
 * @param {string} className - Additional CSS classes for container
 */
function MarketAlignmentAnalysis({ 
  positions, 
  shortenPositionName, 
  visibleSources = {
    compensationPartner: true,
    kununu: true,
    gehaltDe: true
  },
  showTitle = true,
  className = ""
}) {
  if (!positions || positions.length <= 1) {
    return (
      <div className={`text-center py-8 text-gray-500 ${className}`}>
        <p>Mindestens zwei Positionen erforderlich für Markt-Alignment Analyse.</p>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg shadow overflow-hidden ${className}`}>
      <div className="p-6">
        {showTitle && (
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-3">📊</span>
              <h4 className="text-lg font-semibold">Markt-Alignment Analyse (Total Cash)</h4>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>💡 Vergleichsmethodik:</strong> Alle Gehaltsprogressionen werden auf Total Cash Basis verglichen für konsistente Marktanalyse.
              </p>
            </div>
          </div>
        )}
        
        {positions.slice(0, -1).map((position, index) => {
          const nextPosition = positions[index + 1];
          // Use Total Cash for all comparisons for consistency
          const currentDiffPercent = ((nextPosition.salary.peerigonCurrentTotalCash / position.salary.peerigonCurrentTotalCash) - 1) * 100;
          const currentDiffEuro = nextPosition.salary.peerigonCurrentTotalCash - position.salary.peerigonCurrentTotalCash;
          
          // Calculate progressions for all market sources (using Total Cash equivalents)
          const cpDiffPercent = ((nextPosition.salary.medianTotalCash / position.salary.medianTotalCash) - 1) * 100;
          const cpDiffEuro = nextPosition.salary.medianTotalCash - position.salary.medianTotalCash;
          
          // Kununu and Gehalt.de averageSalary represents Total Cash equivalent
          const kununuDiffPercent = position.kununu && nextPosition.kununu ? 
            ((nextPosition.kununu.averageSalary / position.kununu.averageSalary) - 1) * 100 : null;
          const kununuDiffEuro = position.kununu && nextPosition.kununu ? 
            nextPosition.kununu.averageSalary - position.kununu.averageSalary : null;
          
          const gehaltDeDiffPercent = position.gehaltDe && nextPosition.gehaltDe ? 
            ((nextPosition.gehaltDe.averageSalary / position.gehaltDe.averageSalary) - 1) * 100 : null;
          const gehaltDeDiffEuro = position.gehaltDe && nextPosition.gehaltDe ? 
            nextPosition.gehaltDe.averageSalary - position.gehaltDe.averageSalary : null;
          
          // Calculate average market progression (using available and visible sources)
          const availableMarketSources = [];
          if (visibleSources.compensationPartner && cpDiffPercent !== null) availableMarketSources.push(cpDiffPercent);
          if (visibleSources.kununu && kununuDiffPercent !== null) availableMarketSources.push(kununuDiffPercent);
          if (visibleSources.gehaltDe && gehaltDeDiffPercent !== null) availableMarketSources.push(gehaltDeDiffPercent);
          
          const avgMarketDiffPercent = availableMarketSources.length > 0 ? 
            availableMarketSources.reduce((sum, p) => sum + p, 0) / availableMarketSources.length : cpDiffPercent;
          
          // Calculate deviation from market average
          const deviationPercent = currentDiffPercent - avgMarketDiffPercent;
          const deviationCategory = Math.abs(deviationPercent) < 5 ? 'aligned' : 
                                   Math.abs(deviationPercent) < 15 ? 'moderate' : 'significant';
          
          // Determine if requirements change at this level
          const hasRequirementChanges = [
            getCommunicationFrequencyLevel(nextPosition.kommunikation) !== getCommunicationFrequencyLevel(position.kommunikation),
            getCommunicationStyleLevel(nextPosition.kommunikation) !== getCommunicationStyleLevel(position.kommunikation),
            getStrategicInfluenceLevel(nextPosition.komplexität) !== getStrategicInfluenceLevel(position.komplexität),
            getResultVisibilityLevel(nextPosition.komplexität) !== getResultVisibilityLevel(position.komplexität)
          ].some(Boolean);
          
          return (
            <div key={`${position.positionsBezeichnung}-${nextPosition.positionsBezeichnung}`} 
                 className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-orange-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">{shortenPositionName(position.positionsBezeichnung)}</span>
                  <span className="mx-2 text-gray-400">→</span>
                  <span className="font-medium text-gray-700">{shortenPositionName(nextPosition.positionsBezeichnung)}</span>
                  {hasRequirementChanges && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      📋 Anforderungen ändern sich
                    </span>
                  )}
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  deviationCategory === 'aligned' ? 'bg-green-100 text-green-700' :
                  deviationCategory === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {deviationCategory === 'aligned' ? '✅ Markt-konform' :
                   deviationCategory === 'moderate' ? '⚠️ Leichte Abweichung' :
                   '🚨 Starke Abweichung'}
                </div>
              </div>
              
              {/* Primary Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Peerigon Progression (Total Cash)</div>
                  <div className={`text-xl font-bold ${currentDiffPercent >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    +{currentDiffPercent.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">
                    +{currentDiffEuro.toLocaleString()} € / Jahr
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">Markt-Durchschnitt</div>
                  <div className={`text-xl font-bold ${avgMarketDiffPercent >= 0 ? 'text-gray-700' : 'text-red-600'}`}>
                    +{avgMarketDiffPercent.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">
                    Ø aus {availableMarketSources.length} Quelle{availableMarketSources.length > 1 ? 'n' : ''}
                  </div>
                </div>
                
                <div className={`p-3 rounded border ${
                  deviationCategory === 'aligned' ? 'bg-green-50 border-green-200' :
                  deviationCategory === 'moderate' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="text-sm text-gray-600 mb-1">Abweichung vom Markt</div>
                  <div className={`text-xl font-bold ${
                    deviationCategory === 'aligned' ? 'text-green-600' :
                    deviationCategory === 'moderate' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {deviationPercent > 0 ? '+' : ''}{deviationPercent.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">
                    {deviationPercent > 0 ? 'Über' : 'Unter'} Markt-Standard
                  </div>
                </div>
              </div>
              
              {/* Detailed Market Sources Breakdown */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-sm font-medium text-gray-700 mb-3">📈 Markt-Quellen Details</div>
                <div className={`grid gap-3 ${
                  [visibleSources.compensationPartner, visibleSources.kununu, visibleSources.gehaltDe].filter(Boolean).length === 3 ? 'grid-cols-1 md:grid-cols-3' :
                  [visibleSources.compensationPartner, visibleSources.kununu, visibleSources.gehaltDe].filter(Boolean).length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                  'grid-cols-1'
                }`}>
                  {/* Compensation Partner */}
                  {visibleSources.compensationPartner && (
                    <div className="bg-white p-3 rounded border border-green-200">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-600">CP (Total Cash)</div>
                        <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded">Primär</span>
                      </div>
                      <div className={`text-lg font-bold ${cpDiffPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        +{cpDiffPercent.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">
                        +{cpDiffEuro.toLocaleString()} € / Jahr
                      </div>
                    </div>
                  )}
                  
                  {/* Kununu */}
                  {visibleSources.kununu && (
                    <div className={`p-3 rounded border ${kununuDiffPercent === null ? 'bg-gray-100 border-gray-200' : 'bg-white border-blue-200'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-600">Kununu (Total)</div>
                        <span className={`text-xs px-1 py-0.5 rounded ${kununuDiffPercent === null ? 'bg-gray-200 text-gray-500' : 'bg-blue-100 text-blue-700'}`}>
                          {kununuDiffPercent === null ? 'N/A' : 'Community'}
                        </span>
                      </div>
                      {kununuDiffPercent === null ? (
                        <div className="text-sm text-gray-400">Keine Daten</div>
                      ) : (
                        <>
                          <div className={`text-lg font-bold ${kununuDiffPercent >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            +{kununuDiffPercent.toFixed(1)}%
                          </div>
                          <div className="text-xs text-gray-500">
                            +{kununuDiffEuro.toLocaleString()} € / Jahr
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Gehalt.de */}
                  {visibleSources.gehaltDe && (
                    <div className={`p-3 rounded border ${gehaltDeDiffPercent === null ? 'bg-gray-100 border-gray-200' : 'bg-white border-orange-200'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-600">Gehalt.de (Total)</div>
                        <span className={`text-xs px-1 py-0.5 rounded ${gehaltDeDiffPercent === null ? 'bg-gray-200 text-gray-500' : 'bg-orange-100 text-orange-700'}`}>
                          {gehaltDeDiffPercent === null ? 'N/A' : 'Markt'}
                        </span>
                      </div>
                      {gehaltDeDiffPercent === null ? (
                        <div className="text-sm text-gray-400">Keine Daten</div>
                      ) : (
                        <>
                          <div className={`text-lg font-bold ${gehaltDeDiffPercent >= 0 ? 'text-orange-600' : 'text-red-600'}`}>
                            +{gehaltDeDiffPercent.toFixed(1)}%
                          </div>
                          <div className="text-xs text-gray-500">
                            +{gehaltDeDiffEuro.toLocaleString()} € / Jahr
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Detailed Analysis */}
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <div className="text-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-700 mb-1">Analyse:</div>
                      <div className="text-gray-600">
                        {deviationCategory === 'aligned' && 
                          `Die Gehaltserhöhung ist marktkonform und entspricht den Erwartungen für diese Karrierestufe.`
                        }
                        {deviationCategory === 'moderate' && 
                          `Die Progression weicht um ${Math.abs(deviationPercent).toFixed(1)}% vom Marktdurchschnitt (${availableMarketSources.length} Quellen) ab. ${
                            deviationPercent > 0 ? 'Peerigon zahlt über Markt, was Talentbindung unterstützt.' : 
                            'Markt zahlt mehr, mögliches Risiko für Talentabwanderung.'
                          }`
                        }
                        {deviationCategory === 'significant' && 
                          `Signifikante Abweichung von ${Math.abs(deviationPercent).toFixed(1)}% vom Markt. ${
                            deviationPercent > 0 ? 'Überdurchschnittliche Progression, hohe Kosten.' : 
                            'Unterdurchschnittliche Progression, Wettbewerbsnachteil.'
                          } Prüfung der Strategie empfohlen.`
                        }
                      </div>
                    </div>
                    {hasRequirementChanges && (
                      <div className="ml-4 text-xs text-blue-600">
                        <div className="font-medium mb-1">Anforderungs-Änderungen:</div>
                        <div className="space-y-1">
                          {getCommunicationFrequencyLevel(nextPosition.kommunikation) !== getCommunicationFrequencyLevel(position.kommunikation) && 
                            <div>• Kommunikations-Umfang</div>
                          }
                          {getCommunicationStyleLevel(nextPosition.kommunikation) !== getCommunicationStyleLevel(position.kommunikation) && 
                            <div>• Kommunikations-Art</div>
                          }
                          {getStrategicInfluenceLevel(nextPosition.komplexität) !== getStrategicInfluenceLevel(position.komplexität) && 
                            <div>• Strategischer Einfluss</div>
                          }
                          {getResultVisibilityLevel(nextPosition.komplexität) !== getResultVisibilityLevel(position.komplexität) && 
                            <div>• Ergebnisse Sichtbarkeit</div>
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default memo(MarketAlignmentAnalysis);